import { t } from '../i18n';

/**
 * UpdatePrompt — the "a new version is ready" affordance (issue 293).
 *
 * A deployed version can no longer install itself over a running tab (issue 292
 * stopped that), which is what kept the old chunks alive underneath the page.
 * The cost is that the new version now sits waiting until somebody says go, so
 * this is the somebody-says-go: a small, non-blocking bar that asks, and gets
 * out of the way if ignored.
 *
 * Mounted on document.body from the registration code, NOT inside AppShell:
 * the shell is torn down and rebuilt on every route change, which would eat the
 * prompt the moment the user navigated.
 *
 * Non-blocking on purpose. The page still works on the version it has — nothing
 * here is urgent enough to interrupt what someone is in the middle of, and a
 * modal would be. role="status" so it is announced without stealing focus.
 */
export class UpdatePrompt {
  private root: HTMLElement | null = null;
  private textEl: HTMLParagraphElement | null = null;
  // The Reload button's own listener never changes — it always calls through
  // this indirection — so a re-offer while shown (issue 440) only has to
  // repoint the reference, not touch the DOM listener.
  private onReload: (() => void) | null = null;

  /**
   * Show the bar. `version` names the release being offered (issue 409) — on
   * dev the footer already shows the freshly-fetched shell's version, so a
   * bare "new version is ready" reads as a false alarm; naming the offered
   * one makes the banner verifiable. Omitted when the waiting worker
   * predates version reporting.
   *
   * Calling it again while shown does NOT no-op (issue 440): a second deploy
   * can land — and be re-offered by swUpdate.ts — while this bar is still up,
   * naming an OLDER worker the browser has since discarded. Refreshing the
   * text and the Reload target in place (rather than hide-then-re-show) keeps
   * the single-bar guarantee without the visual blink of tearing the bar down
   * for what should read as a quiet update, not a new prompt.
   */
  show(onReload: () => void, version?: string): void {
    this.onReload = onReload;
    const text = version ? t('update.availableVersion', { version }) : t('update.available');

    if (this.root && this.textEl) {
      this.textEl.textContent = text;
      return;
    }

    const bar = document.createElement('div');
    bar.className = 'update-prompt';
    bar.setAttribute('role', 'status');

    const textNode = document.createElement('p');
    textNode.className = 'update-prompt__text';
    // Resolved here, at show time, not at module scope — the locale catalogue
    // loads asynchronously and a module-eval t() freezes to English (issue 287).
    textNode.textContent = text;

    const reload = document.createElement('button');
    reload.type = 'button';
    reload.className = 'btn btn--primary update-prompt__action';
    reload.textContent = t('update.reload');
    // Indirected through `this.onReload` so a later show() call (a re-offer
    // while shown) can repoint Reload without rebinding the DOM listener.
    reload.addEventListener('click', () => this.onReload?.());

    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'btn update-prompt__dismiss';
    dismiss.textContent = t('update.later');
    // Dismissing hides this bar, nothing more: the worker stays waiting and
    // takes over on the next natural reload. "Later" has to mean later, not
    // never and not now.
    dismiss.addEventListener('click', () => this.hide());

    bar.append(textNode, reload, dismiss);
    document.body.appendChild(bar);
    this.root = bar;
    this.textEl = textNode;
  }

  hide(): void {
    this.root?.remove();
    this.root = null;
    this.textEl = null;
    this.onReload = null;
  }

  /** Visible state, for the tests and for callers avoiding a double show. */
  get shown(): boolean {
    return this.root !== null;
  }
}
